import React, { useState, useEffect } from 'react';
import { Table, Input, Select, Space, Card, Typography, DatePicker, Button, message } from 'antd';
import { SearchOutlined, WhatsAppOutlined } from '@ant-design/icons';
import { supabase } from '../../utils/supabase';
import { whatsappApi } from '../../utils/api';
import { format, startOfDay, endOfDay } from 'date-fns';
import locale from 'antd/es/date-picker/locale/fr_FR';

const { Search } = Input;
const { Option } = Select;
const { Text } = Typography;
const { RangePicker } = DatePicker;

const ROWS_PER_PAGE = 50;

const NotificationsTable = () => {
  const [notifications, setNotifications] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [searchField, setSearchField] = useState('phone_number');
  const [dateRange, setDateRange] = useState(null);
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: ROWS_PER_PAGE,
    total: 0,
  });
  const [sorter, setSorter] = useState({
    field: 'sent_at',
    order: 'descend',
  });
  const [sendingMessage, setSendingMessage] = useState(null);

  const fetchNotifications = async (page = 1) => {
    try {
      setLoading(true);
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      // Fetch total count
      let countQuery = supabase
        .from('article_notifications')
        .select('*', { count: 'exact', head: true })
        .eq('user_id', user.id);

      if (searchTerm) {
        countQuery = countQuery.ilike(searchField, `%${searchTerm}%`);
      }

      // Ajouter le filtre de date
      if (dateRange && dateRange[0] && dateRange[1]) {
        const startDate = startOfDay(dateRange[0].$d);
        const endDate = endOfDay(dateRange[1].$d);
        countQuery = countQuery
          .gte('sent_at', startDate.toISOString())
          .lte('sent_at', endDate.toISOString());
      }

      const { count, error: countError } = await countQuery;
      
      if (countError) throw countError;

      // Fetch paginated data
      const from = (page - 1) * ROWS_PER_PAGE;
      const to = from + ROWS_PER_PAGE - 1;

      let query = supabase
        .from('article_notifications')
        .select('*')
        .eq('user_id', user.id)
        .range(from, to);

      // Appliquer le tri
      if (sorter.field && sorter.order) {
        const ascending = sorter.order === 'ascend';
        query = query.order(sorter.field, { ascending });
      }

      if (searchTerm) {
        query = query.ilike(searchField, `%${searchTerm}%`);
      }

      // Ajouter le filtre de date
      if (dateRange && dateRange[0] && dateRange[1]) {
        const startDate = startOfDay(dateRange[0].$d);
        const endDate = endOfDay(dateRange[1].$d);
        query = query
          .gte('sent_at', startDate.toISOString())
          .lte('sent_at', endDate.toISOString());
      }

      const { data, error } = await query;
      
      if (error) throw error;
      
      setNotifications(data || []);
      setPagination(prev => ({
        ...prev,
        current: page,
        total: count || 0,
      }));
    } catch (error) {
      console.error('Error fetching notifications:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchNotifications(1);
  }, []); // Initial fetch

  const handleSearch = () => {
    setPagination(prev => ({ ...prev, current: 1 }));
    fetchNotifications(1);
  };

  const handleTableChange = (pagination, filters, sorter) => {
    setPagination(pagination);
    setSorter({
      field: sorter.field,
      order: sorter.order,
    });
    fetchNotifications(pagination.current);
  };

  const handleDateChange = (dates) => {
    setDateRange(dates);
  };

  const handleSendWhatsApp = async (record) => {
    console.log('Sending message to:', record);
    try {
      setSendingMessage(record.id);
      const response = await whatsappApi.sendAvailability({
        phoneNumber: record.phone_number,
        articleName: record.product_name,
        notificationId: record.id,
        articleUrl: record.article_url,
        size: record.size
      });
      if (response.data.success) {
        message.success('Message WhatsApp envoyé avec succès');
        // Rafraîchir les données du tableau
        fetchNotifications(pagination.current);
      }
    } catch (error) {
      console.error('Error sending WhatsApp message:', error);
      message.error('Erreur lors de l\'envoi du message WhatsApp');
    } finally {
      setSendingMessage(null);
    }
  };

  const columns = [
    {
      title: 'Date d\'envoi',
      dataIndex: 'sent_at',
      key: 'sent_at',
      sorter: true,
      defaultSortOrder: 'descend',
      render: (text) => format(new Date(text), 'dd/MM/yyyy HH:mm'),
    },
    {
      title: 'Numéro',
      dataIndex: 'phone_number',
      key: 'phone_number',
      sorter: true,
    },
    {
      title: 'Produit',
      dataIndex: 'product_name',
      key: 'product_name',
      sorter: true,
    },
    {
      title: 'Taille',
      dataIndex: 'size',
      key: 'size',
      sorter: true,
    },
    {
      title: 'Couleur',
      dataIndex: 'color',
      key: 'color',
      sorter: true,
      render: (text) => text || '-',
    },
    {
      title: 'Statut',
      dataIndex: 'status',
      key: 'status',
      sorter: true,
    },
    {
      title: 'URL',
      dataIndex: 'article_url',
      key: 'article_url',
      sorter: true,
      render: (text) => (
        <a
          href={text}
          target="_blank"
          rel="noopener noreferrer"
          style={{
            maxWidth: 200,
            display: 'block',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap'
          }}
        >
          {text}
        </a>
      ),
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, record) => (
        <Button
          type="primary"
          icon={<WhatsAppOutlined />}
          loading={sendingMessage === record.id}
          onClick={() => handleSendWhatsApp(record)}
          style={{ backgroundColor: '#25D366' }}
        >
          Notifier
        </Button>
      ),
    }
  ];

  return (
    <Card>
      <Space direction="vertical" size="middle" style={{ width: '100%' }}>
        <Space align="center" style={{ width: '100%', justifyContent: 'space-between' }}>
          <Space wrap>
            <Search
              placeholder="Rechercher..."
              allowClear
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              style={{ width: 300 }}
            />
            <Select
              value={searchField}
              onChange={setSearchField}
              style={{ width: 200 }}
            >
              <Option value="phone_number">Numéro de téléphone</Option>
              <Option value="article_url">URL de l'article</Option>
              <Option value="size">Taille</Option>
              <Option value="color">Couleur</Option>
              <Option value="product_name">Nom du produit</Option>
            </Select>
            <RangePicker
              locale={locale}
              onChange={handleDateChange}
              format="DD/MM/YYYY"
              placeholder={['Date début', 'Date fin']}
              allowClear
              disabledDate={current => current && current > new Date()}
              showTime={false}
              value={dateRange}
            />
            <Button 
              type="primary" 
              icon={<SearchOutlined />}
              onClick={handleSearch}
              loading={loading}
            >
              Rechercher
            </Button>
          </Space>
          <Text strong>
            {pagination.total} notification{pagination.total > 1 ? 's' : ''} trouvée{pagination.total > 1 ? 's' : ''}
          </Text>
        </Space>

        <Table
          columns={columns}
          dataSource={notifications}
          rowKey="id"
          pagination={pagination}
          onChange={handleTableChange}
          loading={loading}
          locale={{
            emptyText: 'Aucune notification trouvée',
          }}
        />
      </Space>
    </Card>
  );
};

export default NotificationsTable;
